"use client";

import { FoodCreatorLoadingScreen } from "@/components/food-creator/FoodCreatorAuthGuard";
import { CribNoshLogo } from "@/components/ui/CribNoshLogo";
import { api } from "@/convex/_generated/api";
import type { Id } from "@/convex/_generated/dataModel";
import { useSession } from "@/lib/auth/use-session";
import { getConvexErrorMessage } from "@/lib/utils/convex-error-handler";
import { useMutation, useQuery } from "convex/react";
import { CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

type OnboardingStep =
  | "gdpr"
  | "profile"
  | "cuisines"
  | "image"
  | "location"
  | "kitchen";

type OnboardingState = {
  bio: string;
  specialties: string[];
  city: string;
  coordinates: [number, number];
  profileImage: string;
  foodCreatorName: string;
  foodCreatorAddress: string;
  foodCreatorType: string;
  foodCreatorImages: string[];
};

export type FormData = {
  culinaryBackground: {
    experience: string;
    specialties: string[];
    certifications: string[];
    cuisineTypes: string[];
  };
  availability: string[];
  businessRegistration: string;
  hasInsurance: boolean;
};

type SubmissionState =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "success" }
  | { status: "error"; message: string };

const DRAFT_STORAGE_KEY = "cribnosh-web-food-creator-apply-draft-v2";

const cuisineOptions = [
  "Italian",
  "Chinese",
  "Indian",
  "Mexican",
  "Japanese",
  "Thai",
  "American",
  "Mediterranean",
  "Seafood",
  "BBQ",
  "Bakery",
  "Vegetarian",
];

const stepOrder: OnboardingStep[] = [
  "gdpr",
  "profile",
  "cuisines",
  "image",
  "location",
  "kitchen",
];

const stepContent: Record<
  OnboardingStep,
  { title: string; description: string; cta: string }
> = {
  gdpr: {
    title: "Privacy",
    description:
      "We use your details to run your creator account, orders, and payouts.",
    cta: "Agree and continue",
  },
  profile: {
    title: "Set up your creator profile",
    description:
      "Add a short bio now. You will choose the public business name people see in the next setup step.",
    cta: "Continue",
  },
  cuisines: {
    title: "What do you cook?",
    description: "Pick at least one cuisine. You can change this later.",
    cta: "Continue",
  },
  image: {
    title: "Choose an avatar",
    description: "Pick one now or skip.",
    cta: "Continue",
  },
  location: {
    title: "Where do you cook?",
    description: "Add the city and kitchen address Noshers will order from.",
    cta: "Continue",
  },
  kitchen: {
    title: "Your cooking setup",
    description: "Tell us where you cook and how you plan to sell.",
    cta: "Finish setup",
  },
};

const initialState: OnboardingState = {
  bio: "",
  specialties: [],
  city: "",
  coordinates: [0, 0],
  profileImage: "",
  foodCreatorName: "",
  foodCreatorAddress: "",
  foodCreatorType: "",
  foodCreatorImages: [],
};

const normalizeFoodCreatorType = (value?: string) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return "";
  if (["home", "home_creator", "home_food_creator"].includes(normalized)) {
    return "home_creator";
  }
  if (
    [
      "commercial",
      "commercial_takeaway",
      "takeaway",
      "takeaway_seller",
      "takeaway_food_creator",
    ].includes(normalized)
  ) {
    return "commercial_takeaway";
  }
  return normalized;
};

const isTakeawaySellerType = (value?: string) => {
  const normalized = normalizeFoodCreatorType(value);
  return normalized === "commercial_takeaway" || normalized === "takeaway_seller";
};

const readDraft = (): { currentStep: OnboardingStep; data: OnboardingState } | null => {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as {
      currentStep?: OnboardingStep;
      data?: Partial<OnboardingState>;
    };

    const currentStep = stepOrder.includes(parsed.currentStep as OnboardingStep)
      ? (parsed.currentStep as OnboardingStep)
      : "gdpr";

    return {
      currentStep,
      data: {
        ...initialState,
        ...parsed.data,
        specialties: Array.isArray(parsed.data?.specialties)
          ? parsed.data.specialties.filter(
              (value): value is string => typeof value === "string" && value.trim().length > 0,
            )
          : [],
        foodCreatorImages: Array.isArray(parsed.data?.foodCreatorImages)
          ? parsed.data.foodCreatorImages.filter(
              (value): value is string => typeof value === "string" && value.trim().length > 0,
            )
          : [],
        coordinates:
          Array.isArray(parsed.data?.coordinates) &&
          parsed.data.coordinates.length === 2 &&
          parsed.data.coordinates.every((value) => typeof value === "number")
            ? [parsed.data.coordinates[0], parsed.data.coordinates[1]]
            : [0, 0],
      },
    };
  } catch {
    return null;
  }
};

export default function CookingApplyClient() {
  const router = useRouter();
  const { user, sessionToken, isLoading: isSessionLoading } = useSession();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>("gdpr");
  const [formData, setFormData] = useState<OnboardingState>(initialState);
  const [submissionState, setSubmissionState] = useState<SubmissionState>({
    status: "idle",
  });

  const updateUser = useMutation(api.mutations.users.updateUser);
  const createFoodCreator = useMutation(api.mutations.foodCreators.createFoodCreator);
  const updateFoodCreator = useMutation(api.mutations.foodCreators.update);
  const updateFoodCreatorProfile = useMutation(
    api.mutations.foodCreators.updateFoodCreatorProfile,
  );

  const foodCreator = useQuery(
    api.queries.foodCreators.getByUserId,
    user?._id && sessionToken
      ? {
          userId: user._id as Id<"users">,
          sessionToken,
        }
      : "skip",
  );
  const isBasicOnboardingComplete = useQuery(
    api.queries.foodCreators.isBasicOnboardingComplete,
    foodCreator?._id && sessionToken
      ? {
          foodCreatorId: foodCreator._id,
          sessionToken,
        }
      : "skip",
  );
  const isOnboardingComplete = useQuery(
    api.queries.foodCreatorCourses.isOnboardingComplete,
    foodCreator?._id && sessionToken
      ? {
          foodCreatorId: foodCreator._id,
          sessionToken,
        }
      : "skip",
  );

  const signInReturnTo =
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}`
      : "/become-a-food-creator/apply";

  const currentStepIndex = stepOrder.indexOf(currentStep);
  const currentStepMeta = stepContent[currentStep];

  useEffect(() => {
    const draft = readDraft();
    if (!draft) return;
    setCurrentStep(draft.currentStep);
    setFormData((prev) => ({ ...prev, ...draft.data }));
  }, []);

  useEffect(() => {
    if (!user) return;

    setFormData((prev) => ({
      ...prev,
      foodCreatorName:
        prev.foodCreatorName ||
        String(user.name || "")
          .trim(),
    }));
  }, [user]);

  useEffect(() => {
    if (!foodCreator) return;

    setFormData((prev) => ({
      ...prev,
      bio: prev.bio || foodCreator.bio || "",
      specialties:
        prev.specialties.length > 0 ? prev.specialties : foodCreator.specialties || [],
      city: prev.city || foodCreator.location?.city || "",
      coordinates:
        prev.coordinates[0] !== 0 || prev.coordinates[1] !== 0
          ? prev.coordinates
          : ((foodCreator.location?.coordinates as [number, number] | undefined) || [0, 0]),
      profileImage: prev.profileImage || foodCreator.profileImage || "",
      foodCreatorName: prev.foodCreatorName || foodCreator.name || "",
      foodCreatorAddress: prev.foodCreatorAddress || foodCreator.address || "",
      foodCreatorType:
        prev.foodCreatorType || normalizeFoodCreatorType(foodCreator.foodCreatorType),
      foodCreatorImages:
        prev.foodCreatorImages.length > 0
          ? prev.foodCreatorImages
          : Array.isArray(foodCreator.foodCreatorImages)
            ? foodCreator.foodCreatorImages
            : [],
    }));
  }, [foodCreator]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      DRAFT_STORAGE_KEY,
      JSON.stringify({
        currentStep,
        data: formData,
      }),
    );
  }, [currentStep, formData]);

  useEffect(() => {
    if (isSessionLoading) return;
    if (user?._id && sessionToken) return;

    router.replace(
      `/food-creator/sign-in?notDismissable=true&returnTo=${encodeURIComponent(signInReturnTo)}`,
    );
  }, [isSessionLoading, router, sessionToken, signInReturnTo, user?._id]);

  useEffect(() => {
    if (!user?._id || !sessionToken || isSessionLoading) return;
    if (!foodCreator) return;
    if (isBasicOnboardingComplete === undefined) return;
    if (isBasicOnboardingComplete !== true) return;

    if (isOnboardingComplete === undefined) return;

    router.replace(
      isOnboardingComplete === true
        ? "/food-creator/dashboard"
        : "/food-creator/onboarding",
    );
  }, [
    foodCreator,
    isBasicOnboardingComplete,
    isOnboardingComplete,
    isSessionLoading,
    router,
    sessionToken,
    user?._id,
  ]);

  const isGateLoading =
    isSessionLoading ||
    (!user?._id || !sessionToken) ||
    (foodCreator !== undefined &&
      foodCreator !== null &&
      (isBasicOnboardingComplete === undefined || isOnboardingComplete === undefined));

  const canContinue = useMemo(() => {
    switch (currentStep) {
      case "gdpr":
        return true;
      case "profile":
        return true;
      case "cuisines":
        return formData.specialties.length > 0;
      case "image":
        return true;
      case "location":
        return formData.city.trim().length > 0 && formData.foodCreatorAddress.trim().length > 0;
      case "kitchen":
        return (
          formData.foodCreatorName.trim().length > 0 &&
          formData.foodCreatorType.trim().length > 0
        );
      default:
        return false;
    }
  }, [currentStep, formData]);

  const updateField = <K extends keyof OnboardingState>(key: K, value: OnboardingState[K]) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const toggleCuisine = (cuisine: string) => {
    setFormData((prev) => ({
      ...prev,
      specialties: prev.specialties.includes(cuisine)
        ? prev.specialties.filter((item) => item !== cuisine)
        : [...prev.specialties, cuisine],
    }));
  };

  const goBack = () => {
    if (currentStepIndex <= 0) return;
    setCurrentStep(stepOrder[currentStepIndex - 1]);
  };

  const goNext = async () => {
    if (!canContinue) return;

    if (currentStep === "kitchen") {
      await handleSubmit();
      return;
    }

    setCurrentStep(stepOrder[currentStepIndex + 1]);
  };

  const handleSubmit = async () => {
    if (!user?._id || !sessionToken) return;

    const normalizedFoodCreatorType = normalizeFoodCreatorType(formData.foodCreatorType);
    const isTakeawaySeller = isTakeawaySellerType(normalizedFoodCreatorType);
    const coordinates =
      formData.coordinates[0] !== 0 || formData.coordinates[1] !== 0
        ? formData.coordinates
        : ([0, 0] as [number, number]);

    setSubmissionState({ status: "submitting" });

    try {
      await updateUser({
        userId: user._id as Id<"users">,
        ...(formData.foodCreatorName.trim() ? { name: formData.foodCreatorName.trim() } : {}),
        sessionToken,
      });

      let foodCreatorId = foodCreator?._id;

      if (foodCreatorId) {
        await updateFoodCreator({
          foodCreatorId,
          updates: {
            name: formData.foodCreatorName.trim(),
            bio: formData.bio.trim(),
            specialties: formData.specialties,
            location: {
              city: formData.city.trim(),
              coordinates,
            },
            ...(formData.profileImage.trim()
              ? { profileImage: formData.profileImage.trim() }
              : {}),
            foodCreatorType: normalizedFoodCreatorType,
            isTakeawaySeller,
          },
          sessionToken,
        });
      } else {
        foodCreatorId = await createFoodCreator({
          userId: user._id as Id<"users">,
          onboardingDocumentPolicyVersion: "inspection_pair_v2",
          name: formData.foodCreatorName.trim(),
          bio: formData.bio.trim(),
          specialties: formData.specialties,
          cuisine: formData.specialties,
          location: {
            lat: coordinates[0],
            lng: coordinates[1],
            city: formData.city.trim(),
          },
          ...(formData.profileImage.trim()
            ? { image: formData.profileImage.trim() }
            : {}),
          foodCreatorType: normalizedFoodCreatorType,
          isTakeawaySeller,
          sessionToken,
        });
      }

      await updateFoodCreatorProfile({
        foodCreatorId,
        address: formData.foodCreatorAddress.trim(),
        foodCreatorType: normalizedFoodCreatorType,
        isTakeawaySeller,
        ...(formData.foodCreatorImages.length > 0
          ? { images: formData.foodCreatorImages }
          : {}),
        sessionToken,
      });

      if (typeof window !== "undefined") {
        window.localStorage.removeItem(DRAFT_STORAGE_KEY);
      }
      setSubmissionState({ status: "success" });
    } catch (error) {
      setSubmissionState({
        status: "error",
        message: getConvexErrorMessage(error, "Could not finish creator setup."),
      });
    }
  };

  if (isGateLoading) {
    return <FoodCreatorLoadingScreen label="Loading creator setup..." />;
  }

  if (submissionState.status === "success") {
    return (
      <main className="min-h-screen bg-[#FAFCF8] px-4 py-10 text-[#101828]">
        <div className="mx-auto flex min-h-[80vh] w-full max-w-2xl items-center">
          <div className="w-full rounded-[30px] bg-[#FFF8F1] px-6 py-8 text-center shadow-[0_18px_40px_rgba(17,24,39,0.08)] sm:px-8 sm:py-10">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#ECFDF3] text-[#039855]">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h1 className="mt-6 text-3xl font-semibold">Creator setup saved</h1>
            <p className="mt-3 text-sm leading-6 text-[#475467]">
              Your creator profile is set up. Continue with documents, training, and payouts in onboarding.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <Link
                href="/food-creator/onboarding"
                className="inline-flex items-center justify-center rounded-full bg-[#E94B3C] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#d93d2f]"
              >
                Open onboarding
              </Link>
              <Link
                href="/food-creator/dashboard"
                className="inline-flex items-center justify-center rounded-full bg-white px-5 py-3 text-sm font-semibold text-[#344054] transition hover:bg-[#F9FAFB]"
              >
                Go to dashboard
              </Link>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#FAFCF8] text-[#101828]">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-0 h-72 w-72 rounded-full bg-[#FFE2D7] blur-[110px]" />
        <div className="absolute right-[-3rem] top-0 h-72 w-72 rounded-full bg-[#E8F7EC] blur-[110px]" />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(250,252,248,0.68)_0%,rgba(250,252,248,0.94)_28%,rgba(255,248,241,0.98)_100%)]" />
      </div>

      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-5xl flex-col px-4 py-8 sm:px-6 sm:py-10">
        <div className="mb-8">
          <CribNoshLogo size={156} />
        </div>

        <section className="w-full max-w-3xl rounded-[30px] bg-[#FFF8F1] shadow-[0_18px_40px_rgba(17,24,39,0.08)]">
          <div className="max-h-[78vh] overflow-y-auto px-6 pb-32 pt-10 sm:px-8">
            {currentStepIndex > 0 ? (
              <button
                type="button"
                onClick={goBack}
                className="mb-5 inline-flex items-center gap-2 text-sm font-semibold text-[#02120A]"
              >
                <ChevronLeft className="h-4 w-4" />
                Back
              </button>
            ) : null}

            <div className="mb-6 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#667085]">
                Step {currentStepIndex + 1} of {stepOrder.length}
              </span>
              <span className="text-xs font-medium text-[#667085]">
                {Math.round(((currentStepIndex + 1) / stepOrder.length) * 100)}%
              </span>
            </div>

            <div className="mb-8 h-1.5 rounded-full bg-[#EAECF0]">
              <div
                className="h-full rounded-full bg-[#F23E2E] transition-all"
                style={{ width: `${((currentStepIndex + 1) / stepOrder.length) * 100}%` }}
              />
            </div>

            <h1 className="text-[32px] font-bold leading-[1.1] text-[#111827]">
              {currentStepMeta.title}
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-6 text-[#6B7280]">
              {currentStepMeta.description}
            </p>

            {user?.email ? (
              <div className="mt-6 rounded-2xl bg-white px-4 py-4 text-sm text-[#475467]">
                Signed in as{" "}
                <span className="font-semibold text-[#101828]">{user.email}</span>.
              </div>
            ) : null}

            <div className="mt-8 space-y-6">
              {currentStep === "gdpr" ? (
                <div className="space-y-4 text-sm leading-6 text-[#344054]">
                  <div className="rounded-2xl bg-white px-5 py-5">
                    <p className="font-semibold text-[#101828]">How we use your data</p>
                    <p className="mt-3">
                      We use your details to keep your profile, orders, and payouts working.
                      Your information is stored securely and shared only when it is needed to run CribNosh.
                    </p>
                    <p className="mt-3">
                      By continuing, you agree to our{" "}
                      <Link href="/privacy" className="font-semibold text-[#F23E2E] underline-offset-2 hover:underline">
                        Privacy Policy
                      </Link>{" "}
                      and{" "}
                      <Link href="/terms" className="font-semibold text-[#F23E2E] underline-offset-2 hover:underline">
                        Terms
                      </Link>
                      .
                    </p>
                  </div>
                </div>
              ) : null}

              {currentStep === "profile" ? (
                <div className="space-y-3">
                  <label className="block text-sm font-semibold text-[#344054]" htmlFor="creator-bio">
                    Bio (optional)
                  </label>
                  <textarea
                    id="creator-bio"
                    value={formData.bio}
                    onChange={(event) => updateField("bio", event.target.value)}
                    placeholder="Add a short note, or leave it for later"
                    className="min-h-32 w-full rounded-2xl border border-[#E5E7EB] bg-white px-4 py-4 text-base text-[#101828] outline-none transition placeholder:text-[#98A2B3] focus:border-[#F23E2E] focus:ring-2 focus:ring-[#F23E2E]/10"
                  />
                </div>
              ) : null}

              {currentStep === "cuisines" ? (
                <div className="flex flex-wrap gap-3">
                  {cuisineOptions.map((cuisine) => {
                    const selected = formData.specialties.includes(cuisine);
                    return (
                      <button
                        key={cuisine}
                        type="button"
                        onClick={() => toggleCuisine(cuisine)}
                        className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                          selected
                            ? "border-[#F23E2E] bg-[#F23E2E] text-white"
                            : "border-[#D0D5DD] bg-white text-[#344054] hover:border-[#F23E2E]/50"
                        }`}
                      >
                        {cuisine}
                      </button>
                    );
                  })}
                </div>
              ) : null}

              {currentStep === "image" ? (
                <div className="rounded-2xl bg-white px-5 py-8 text-center">
                  <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#F2F4F7] text-3xl text-[#98A2B3]">
                    {formData.profileImage.trim()
                      ? formData.foodCreatorName.trim().slice(0, 1).toUpperCase() || "C"
                      : "C"}
                  </div>
                  <p className="mt-4 text-sm font-medium text-[#101828]">Avatar selection</p>
                  <p className="mt-2 text-sm leading-6 text-[#667085]">
                    The mobile app lets creators choose an avatar here. On web you can keep going now and update imagery later.
                  </p>
                </div>
              ) : null}

              {currentStep === "location" ? (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-[#344054]" htmlFor="creator-city">
                      City
                    </label>
                    <input
                      id="creator-city"
                      type="text"
                      value={formData.city}
                      onChange={(event) => updateField("city", event.target.value)}
                      placeholder="e.g. Edinburgh"
                      className="mt-2 w-full rounded-2xl border border-[#E5E7EB] bg-white px-4 py-3 text-base text-[#101828] outline-none transition placeholder:text-[#98A2B3] focus:border-[#F23E2E] focus:ring-2 focus:ring-[#F23E2E]/10"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#344054]" htmlFor="creator-address">
                      Kitchen address
                    </label>
                    <textarea
                      id="creator-address"
                      value={formData.foodCreatorAddress}
                      onChange={(event) =>
                        updateField("foodCreatorAddress", event.target.value)
                      }
                      placeholder="Enter the address you cook from"
                      className="mt-2 min-h-28 w-full rounded-2xl border border-[#E5E7EB] bg-white px-4 py-4 text-base text-[#101828] outline-none transition placeholder:text-[#98A2B3] focus:border-[#F23E2E] focus:ring-2 focus:ring-[#F23E2E]/10"
                    />
                  </div>
                </div>
              ) : null}

              {currentStep === "kitchen" ? (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-[#344054]" htmlFor="public-name">
                      Name people will see
                    </label>
                    <input
                      id="public-name"
                      type="text"
                      value={formData.foodCreatorName}
                      onChange={(event) =>
                        updateField("foodCreatorName", event.target.value)
                      }
                      placeholder="e.g. Maria's Kitchen"
                      className="mt-2 w-full rounded-2xl border border-[#E5E7EB] bg-white px-4 py-3 text-base text-[#101828] outline-none transition placeholder:text-[#98A2B3] focus:border-[#F23E2E] focus:ring-2 focus:ring-[#F23E2E]/10"
                    />
                  </div>
                  <div>
                    <span className="block text-sm font-semibold text-[#344054]">
                      Setup type
                    </span>
                    <div className="mt-2 flex flex-wrap gap-3">
                      {[
                        { value: "home_creator", label: "Home creator" },
                        { value: "commercial_takeaway", label: "Takeaway seller" },
                      ].map((option) => {
                        const selected = normalizeFoodCreatorType(formData.foodCreatorType) === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => updateField("foodCreatorType", option.value)}
                            className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                              selected
                                ? "border-[#F23E2E] bg-[#F23E2E] text-white"
                                : "border-[#D0D5DD] bg-white text-[#344054] hover:border-[#F23E2E]/50"
                            }`}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : null}

              {submissionState.status === "error" ? (
                <div className="rounded-2xl bg-[#FEF2F2] px-4 py-4 text-sm text-[#B42318]">
                  {submissionState.message}
                </div>
              ) : null}
            </div>
          </div>

          <div className="sticky bottom-0 flex items-center justify-between gap-3 rounded-t-2xl border-t border-[#F0E7DE] bg-[#FFF8F1] px-6 py-4 sm:px-8">
            <button
              type="button"
              onClick={goBack}
              disabled={currentStepIndex === 0 || submissionState.status === "submitting"}
              className="inline-flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-[#344054] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </button>

            <button
              type="button"
              onClick={goNext}
              disabled={!canContinue || submissionState.status === "submitting"}
              className="inline-flex items-center gap-2 rounded-full bg-[#E94B3C] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#d93d2f] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submissionState.status === "submitting" ? "Saving..." : currentStepMeta.cta}
              {submissionState.status === "submitting" ? null : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          </div>
        </section>
      </div>
    </main>
  );
}
